

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, CreditCard, Crown, RefreshCw, Settings } from 'lucide-react';
import { usePermissions } from '@/contexts/unified-permissions';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { LoadingState } from '@/components/ui/loading-state';
import { pricingConfig } from '@/hooks/subscription/types';
import { isIOSNativeApp } from '@/utils/platformUtils';

export function SubscriptionManagement() {
  const {
    isPremium: isSubscribed,
    permissionsStatus,
    isRefreshing,
    upgradeInProgress,
    initiateSubscription,
    refreshPermissions,
    subscriptionSource
  } = usePermissions();
  const { state } = useOfflineState();

  // Platform detection for Apple App Store compliance
  const isIOSNative = isIOSNativeApp();

  // Determine what subscription management options to show based on platform and subscription source
  // Key logic for Apple App Store compliance:
  // - iOS Native + Free User: Hide all Stripe references (Apple compliance)
  // - iOS Native + Stripe Subscription: Show Stripe management (existing subscription allowed)
  // - iOS Native + iOS IAP Subscription: Show App Store management only
  // - Non-iOS: Show Stripe normally
  const shouldShowStripeManagement = isSubscribed && subscriptionSource === 'stripe'; // Show for existing Stripe subscribers regardless of platform
  const shouldShowIOSManagement = isSubscribed && isIOSNative && subscriptionSource !== 'stripe'; // Show iOS management for non-Stripe subscriptions on iOS
  const shouldHideStripeReferences = isIOSNative && !isSubscribed; // Free iOS users should not see Stripe references

  const handleRefreshStatus = () => {
    if (state.isOnline) {
      refreshPermissions();
    }
  };

  const isLoading = permissionsStatus === 'loading';

  if (isLoading || isRefreshing) {
    return (
      <div className="py-4">
        <LoadingState 
          size="sm" 
          title="Checking subscription status" 
          description="Please wait while we verify your subscription status..." 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Subscription Management</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Manage your subscription and billing settings.
        </p>
      </div>



      <Card>
        <CardHeader className="pb-3 text-left">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-left">Current Status</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshStatus}
              disabled={isRefreshing || !state.isOnline}
              className="h-8 w-8 p-0"
              title={!state.isOnline ? "Refresh requires internet connection" : "Refresh subscription status"}
            >
              <RefreshCw className="h-4 w-4" />
              <span className="sr-only">Refresh</span>
            </Button>
          </div>
          <CardDescription className="text-left">
            Your current membership status and benefits
          </CardDescription>
        </CardHeader>
        <CardContent className="text-left">
          <div className="flex items-center space-x-4">
            <div className={`rounded-full p-2 ${isSubscribed ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'}`}>
              {isSubscribed ? (
                <Crown className="h-6 w-6" />
              ) : (
                <CreditCard className="h-6 w-6" />
              )}
            </div>
            <div className="text-left">
              <div className="flex items-center space-x-2 flex-wrap">
                <h4 className="font-medium text-left">
                  {isSubscribed ? 'Premium Member' : 'Free Account'}
                </h4>
                {isSubscribed && (
                  <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                    Active
                  </Badge>
                )}
              </div>
              {isSubscribed && subscriptionSource && (
                <p className="text-sm text-muted-foreground mt-1 text-left">
                  {subscriptionSource === 'admin_override'
                    ? 'Active through Admin privileges'
                    : isIOSNative
                    ? 'Active through App Store subscription'
                    : 'Active subscription'}
                </p>
              )}
            </div>
          </div>

          {isSubscribed ? (
            <div className="mt-4 space-y-3 text-left">
              <div className="text-sm text-left">
                <div className="font-medium mb-2 text-left">Benefits included:</div>
                <ul className="space-y-2 text-left">
                  <li className="flex items-start text-left">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-left">Premium techniques library access</span>
                  </li>
                  <li className="flex items-start text-left">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-left">Game planning tools</span>
                  </li>
                  <li className="flex items-start text-left">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-left">Advanced training features</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="mt-4 text-left">
              <div className="text-sm text-muted-foreground text-left">
                Upgrade to premium to unlock advanced features including Game Planner and My Techniques.
              </div>
            </div>
          )}
        </CardContent>
        {!isSubscribed && (
          <CardFooter className="border-t pt-4 flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <Button
              onClick={() => initiateSubscription('monthly')}
              disabled={upgradeInProgress || !state.isOnline}
              className="w-full sm:w-auto"
              title={!state.isOnline ? "Subscription requires internet connection" : ""}
            >
              {upgradeInProgress ? 'Processing...' : 'Upgrade to Monthly Plan'}
            </Button>
            <Button
              variant="outline"
              onClick={() => initiateSubscription('annual')}
              disabled={upgradeInProgress || !state.isOnline}
              className="w-full sm:w-auto"
              title={!state.isOnline ? "Subscription requires internet connection" : ""}
            >
              Upgrade to Annual Plan (Save {pricingConfig.annual.savingsPercentage}%)
            </Button>
          </CardFooter>
        )}
        {/* Stripe subscription management - only show for existing Stripe subscribers on non-iOS platforms */}
        {shouldShowStripeManagement && (
          <CardFooter className="border-t pt-4">
            {state.isOnline ? (
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                asChild
              >
                <a href="https://billing.stripe.com/p/login/test_28o14rcIu9QdeDucYY" target="_blank" rel="noopener noreferrer">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Billing
                </a>
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                disabled
                title="Billing management requires internet connection"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Manage Billing (Offline)
              </Button>
            )}
          </CardFooter>
        )}

        {/* iOS App Store subscription management - only for iOS native apps with subscriptions */}
        {shouldShowIOSManagement && (
          <CardFooter className="border-t pt-4">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                // Deep link to App Store subscription management
                window.open('https://apps.apple.com/account/subscriptions', '_system');
              }}
            >
              <Settings className="mr-2 h-4 w-4" />
              Manage Subscription
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* FAQ Section - Platform-aware content */}
      <div className="mt-6 text-left">
        <h4 className="font-medium mb-3 text-left">Frequently Asked Questions</h4>
        <div className="space-y-4 text-left">
          <div className="text-left">
            <h5 className="font-medium text-sm text-left">How do I cancel my subscription?</h5>
            <p className="text-sm text-muted-foreground mt-1 text-left">
              {shouldHideStripeReferences ? (
                <>
                  You can cancel your subscription at any time through your App Store account settings.
                  Go to Settings → [Your Name] → Subscriptions, or tap "Manage Subscription" above.
                  Your premium access will continue until the end of your current billing period.
                </>
              ) : isIOSNative && isSubscribed ? (
                <>
                  {subscriptionSource === 'stripe' ? (
                    <>You can cancel your subscription through your account settings. Your premium access will continue until the end of your current billing period.</>
                  ) : (
                    <>You can cancel your subscription through your App Store account settings. Go to Settings → [Your Name] → Subscriptions, or tap "Manage Subscription" above. Your premium access will continue until the end of your current billing period.</>
                  )}
                </>
              ) : (
                <>
                  You can cancel your subscription at any time through your account settings.
                  Your premium access will continue until the end of your current billing period.
                </>
              )}
            </p>
          </div>
          <div className="text-left">
            <h5 className="font-medium text-sm text-left">When will I be billed?</h5>
            <p className="text-sm text-muted-foreground mt-1 text-left">
              {shouldHideStripeReferences ? (
                <>
                  Payment will be charged to your Apple ID account at confirmation of purchase.
                  For monthly subscriptions, you'll be billed on the same day each month.
                  For annual subscriptions, you'll be billed once a year on your subscription anniversary.
                </>
              ) : (
                <>
                  For monthly subscriptions, you'll be billed on the same day each month.
                  For annual subscriptions, you'll be billed once a year on your subscription anniversary.
                </>
              )}
            </p>
          </div>
          {shouldHideStripeReferences && (
            <div className="text-left">
              <h5 className="font-medium text-sm text-left">Auto-Renewal Information</h5>
              <p className="text-sm text-muted-foreground mt-1 text-left">
                Subscription automatically renews unless auto-renew is turned off at least 24-hours
                before the end of the current period. You can manage auto-renewal in your App Store
                account settings after purchase.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
